import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

/**
 * The one hand-placed AdSense slot, used at the top of the article template and
 * twice on the homepage.
 *
 * Two things about it were costing layout stability, both fixed here (2026-09-05):
 *
 * 1. It reserved no height. Google's responsive rewrite renders the <ins> at 375px
 *    below 768px, it resolves at ~5.2s, and it keeps its full height even when it
 *    does not fill (data-ad-status="unfill-optimized"). Arriving that late into a
 *    zero-height box pushed everything under it down 412px — measured CLS 0.579 on
 *    the desktop homepage and 0.397-0.496 on mobile. The box is now reserved up
 *    front. Slot ID, format and inventory are unchanged: this costs no revenue.
 *
 * 2. It rendered nothing while auth was still loading, so anonymous readers got a
 *    third shift when the slot appeared. `isAuthenticated` is derived from a token
 *    read synchronously out of localStorage, so it is already correct on the first
 *    render and the `loading` guard was never needed for the render decision — only
 *    for deferring the adsbygoogle push.
 *
 * Heights measured off the live rendered <ins> at 375x812 and at desktop widths.
 */
const MobileArticleAd: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const pushed = useRef(false);

  useEffect(() => {
    if (loading || isAuthenticated) return;
    if (pushed.current) return;
    if (typeof window === 'undefined') return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // adsbygoogle.js may not be loaded yet — push retries on the next render
    }
  }, [isAuthenticated, loading]);

  // Signed-in readers never see the slot, so no space is held for them.
  if (isAuthenticated) return null;

  return (
    <div className="min-h-[412px] md:min-h-[280px]">
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-9243961427413079"
        data-ad-slot="9442268600"
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default MobileArticleAd;
